import React, { createContext, useContext, useState, useEffect } from "react";
import { onAuthStateChanged, signInWithPopup, signOut as firebaseSignOut } from "firebase/auth";
import { auth, googleProvider, isFirebaseConfigured } from "./firebase";
import { isEmailAllowed } from "./allowedEmails";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const allowed = user ? isEmailAllowed(user.email) : false;

  useEffect(() => {
    if (!isFirebaseConfigured()) {
      setAuthLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthError(null);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    setAuthError(null);
    if (!isFirebaseConfigured()) {
      setAuthError("Firebase is not configured. Add REACT_APP_FIREBASE_* env vars.");
      return;
    }
    setIsSigningIn(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") {
        setAuthError("Sign-in cancelled.");
      } else {
        setAuthError(err.message || "Sign-in failed.");
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  const signOut = async () => {
    setAuthError(null);
    if (!isFirebaseConfigured()) return;
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      setAuthError(err.message || "Sign-out failed.");
    }
  };

  const value = {
    user,
    allowed,
    authLoading,
    authError,
    isSigningIn,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
